import { gql, type TypedDocumentNode } from '@apollo/client';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link, useTypedParams } from '@sorare/routing';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Row } from '@sorare/core/src/atoms/layout/Table';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { TokenMetas } from '@sorare/core/src/components/token/TokenMetas';
import {
  HELP,
  MY_SORARE_PURCHASES_INSTANT_BUYS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { FlexCard } from '@sorare/marketplace/src/components/token/FlexCard';

import type {
  BoughtPrimaryOfferQuery,
  BoughtPrimaryOfferQueryVariables,
} from './__generated__/page.graphql.ts';

const Wrapper = styled(Vertical).attrs({ gap: 4 })`
  border: 1px solid var(--c-nd-200);
  padding: var(--double-unit);
  border-radius: var(--half-unit);
  margin: var(--triple-unit);
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--double-unit);
  width: 100%;

  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
  }
`;
const ItemsGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--double-unit);
`;

const BOUGHT_PRIMARY_OFFER_QUERY = gql`
  query BoughtPrimaryOfferQuery($id: String!) {
    tokens {
      primaryOffer(id: $id) {
        id
        acceptedAt
        price {
          ...useAmountWithConversion_monetaryAmount
        }
        anyCards {
          slug
          anyPlayer {
            slug
            displayName
          }
          ...FlexCard_anyCard
          ...TokenMetas_anyCard
        }
      }
    }
  }
  ${FlexCard.fragments.anyCard}
  ${TokenMetas.fragments.anyCard}
  ${useAmountWithConversion.fragments.monetaryAmount}
` as TypedDocumentNode<
  BoughtPrimaryOfferQuery,
  BoughtPrimaryOfferQueryVariables
>;

export const MyPrimaryOffersId = () => {
  const { id } = useTypedParams();
  const { data } = useQuery(BOUGHT_PRIMARY_OFFER_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id,
    },
  });

  const primaryOffer = data?.tokens.primaryOffer;

  const { main } = useAmountWithConversion({
    monetaryAmount: primaryOffer?.price,
  });

  if (!primaryOffer) return null;

  return (
    <Wrapper>
      <HeadlineS>
        <Link to={MY_SORARE_PURCHASES_INSTANT_BUYS}>
          <Horizontal>
            <FontAwesomeIcon icon={faAngleLeft} />
            {!!primaryOffer.acceptedAt && (
              <FormattedMessage
                defaultMessage="Purchase from {date}"
                values={{
                  date: (
                    <FormattedDate
                      value={primaryOffer.acceptedAt}
                      month="short"
                      day="numeric"
                      hour="2-digit"
                      minute="2-digit"
                      year="numeric"
                    />
                  ),
                }}
              />
            )}
          </Horizontal>
        </Link>
      </HeadlineS>
      <Layout>
        <Box style={{ paddingTop: 0, minWidth: '300px' }}>
          {primaryOffer.anyCards.length > 1 && (
            <Row>
              <LabelS bold color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="Number of cards" />
              </LabelS>
              <LabelS bold>{primaryOffer.anyCards.length}</LabelS>
            </Row>
          )}
          <Row>
            <HeadlineS bold>
              <FormattedMessage defaultMessage="Order total" />
            </HeadlineS>
            <HeadlineS bold>{main}</HeadlineS>
          </Row>

          <Button to={HELP} color="quaternary" size="medium">
            <FormattedMessage defaultMessage="Need assistance?" />
          </Button>
        </Box>

        <ItemsGrid>
          {primaryOffer.anyCards.map(card => (
            <Horizontal key={card.slug}>
              <FlexCard card={card} width={80} />
              <Vertical>
                <BrandS>{card.anyPlayer.displayName}</BrandS>
                <LabelS color="var(--c-nd-600)">
                  <TokenMetas card={card} separator={' \u2013 '} />
                </LabelS>
              </Vertical>
            </Horizontal>
          ))}
        </ItemsGrid>
      </Layout>
    </Wrapper>
  );
};

export default MyPrimaryOffersId;
